import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import './JobApplicationModal.scss';

interface CV {
  id: number;
  title: string;
  is_primary: boolean;
  first_name: string;
  last_name: string;
}

interface CoverLetter {
  id: number;
  title: string;
  content: string;
  is_primary: boolean;
}

interface JobApplicationModalProps {
  jobId: number;
  jobTitle: string;
  companyName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const JobApplicationModal: React.FC<JobApplicationModalProps> = ({
  jobId,
  jobTitle,
  companyName,
  onClose,
  onSuccess
}) => {
  const [cvs, setCvs] = useState<CV[]>([]);
  const [coverLetters, setCoverLetters] = useState<CoverLetter[]>([]);
  const [selectedCvId, setSelectedCvId] = useState<number | null>(null);
  const [selectedCoverLetterId, setSelectedCoverLetterId] = useState<number | null>(null);
  const [additionalMessage, setAdditionalMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const [cvsResponse, lettersResponse] = await Promise.all([
        fetch(`${API_URL}/cv/my`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_URL}/cover-letters/my`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (cvsResponse.ok) {
        const cvsResult = await cvsResponse.json();
        setCvs(cvsResult.data);
        
        // Auto-select primary CV if available
        const primaryCV = cvsResult.data.find((cv: CV) => cv.is_primary);
        if (primaryCV) {
          setSelectedCvId(primaryCV.id);
        } else if (cvsResult.data.length === 1) {
          setSelectedCvId(cvsResult.data[0].id);
        }
      }

      if (lettersResponse.ok) {
        const lettersResult = await lettersResponse.json();
        setCoverLetters(lettersResult.data);
        
        // Auto-select primary cover letter if available
        const primaryLetter = lettersResult.data.find((letter: CoverLetter) => letter.is_primary);
        if (primaryLetter) {
          setSelectedCoverLetterId(primaryLetter.id);
        } else if (lettersResult.data.length === 1) {
          setSelectedCoverLetterId(lettersResult.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Nepavyko įkelti duomenų');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCvId) {
      setError('Prašome pasirinkti CV');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/job-applications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_id: jobId,
          cv_id: selectedCvId,
          cover_letter_id: selectedCoverLetterId,
          additional_message: additionalMessage
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const result = await response.json();
        setError(result.message || 'Nepavyko pateikti paraiškos');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setError('Įvyko klaida pateikiant paraišką');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content application-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Kandidatuoti į poziciją</h2>
            <p className="job-info">{jobTitle}{companyName ? ` • ${companyName}` : ''}</p>
          </div>
          <button className="btn-close" onClick={onClose} title="Uždaryti">
            <X size={24} />
          </button>
        </div>

        {loading ? (
          <div className="modal-body">
            <div className="loading">Kraunama...</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}

              {cvs.length === 0 && (
                <div className="warning-message">
                  <p>Neturite sukurto CV. Prašome pirmiausia sukurti CV.</p>
                  <a href="/mano-cv" className="btn-link">Sukurti CV</a>
                </div>
              )}

              {cvs.length > 0 && (
                <>
                  <div className="form-group">
                    <label>Pasirinkite CV *</label>
                    <div className="cv-selection">
                      {cvs.map((cv) => (
                        <div
                          key={cv.id}
                          className={`cv-option ${selectedCvId === cv.id ? 'selected' : ''}`}
                          onClick={() => setSelectedCvId(cv.id)}
                        >
                          <input
                            type="radio"
                            name="cv"
                            value={cv.id}
                            checked={selectedCvId === cv.id}
                            onChange={() => setSelectedCvId(cv.id)}
                          />
                          <div className="cv-option-content">
                            <div className="cv-option-title">
                              {cv.title}
                              {cv.is_primary && <span className="badge">Pagrindinis</span>}
                            </div>
                            <div className="cv-option-name">{cv.first_name} {cv.last_name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Pasirinkite motyvacinį laišką (neprivaloma)</label>
                    {coverLetters.length === 0 ? (
                      <div className="info-message">
                        <p>Neturite sukurtų motyvacinių laiškų.</p>
                        <a href="/mano-cv" className="btn-link">Sukurti motyvacinį laišką</a>
                      </div>
                    ) : (
                      <div className="cover-letter-selection">
                        <div
                          className={`cover-letter-option ${selectedCoverLetterId === null ? 'selected' : ''}`}
                          onClick={() => setSelectedCoverLetterId(null)}
                        >
                          <input
                            type="radio"
                            name="coverLetter"
                            value=""
                            checked={selectedCoverLetterId === null}
                            onChange={() => setSelectedCoverLetterId(null)}
                          />
                          <div className="cover-letter-option-content">
                            <div className="cover-letter-option-title">Nesiųsti motyvacinio laiško</div>
                          </div>
                        </div>
                        {coverLetters.map((letter) => (
                          <div
                            key={letter.id}
                            className={`cover-letter-option ${selectedCoverLetterId === letter.id ? 'selected' : ''}`}
                            onClick={() => setSelectedCoverLetterId(letter.id)}
                          >
                            <input
                              type="radio"
                              name="coverLetter"
                              value={letter.id}
                              checked={selectedCoverLetterId === letter.id}
                              onChange={() => setSelectedCoverLetterId(letter.id)}
                            />
                            <div className="cover-letter-option-content">
                              <div className="cover-letter-option-title">
                                {letter.title}
                                {letter.is_primary && <span className="badge">Pagrindinis</span>}
                              </div>
                              <div className="cover-letter-preview">
                                {letter.content.substring(0, 100)}...
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Papildoma žinutė (neprivaloma)</label>
                    <textarea
                      value={additionalMessage}
                      onChange={(e) => setAdditionalMessage(e.target.value)}
                      placeholder="Įveskite papildomą informaciją arba klausimus darbdaviui..."
                      rows={4}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Atšaukti
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting || !selectedCvId || cvs.length === 0}
              >
                {submitting ? 'Siunčiama...' : 'Kandidatuoti'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default JobApplicationModal;

